// konva/overlays/PenOverlay.ts — Visual preview while drawing with the pen tool
import Konva from 'konva';
import type { PathPoint } from '../../core/tools/pathUtils';
import { pathPointsToSVG } from '../../core/tools/pathUtils';
import type { Point } from '../../core/tools/types';

export class PenOverlay {
  private path: Konva.Path | null = null;
  private pointCircles: Konva.Circle[] = [];
  private handleLines: Konva.Line[] = [];
  private handleCircles: Konva.Circle[] = [];
  private closeIndicator: Konva.Circle | null = null;
  private previewLine: Konva.Line | null = null;
  private layer: Konva.Layer;

  constructor(layer: Konva.Layer) {
    this.layer = layer;
  }

  update(points: PathPoint[], currentMouse?: Point, nearFirstPoint?: boolean): void {
    this.clearAll();

    if (points.length === 0) return;

    // Draw the committed path segments
    if (points.length >= 2) {
      const d = pathPointsToSVG(points, false);
      this.path = new Konva.Path({
        data: d,
        stroke: '#2563eb',
        strokeWidth: 2,
        fill: '',
        listening: false,
      });
      this.layer.add(this.path);
    }

    // Draw preview line from last point to current mouse
    if (currentMouse && points.length >= 1) {
      const last = points[points.length - 1];
      const linePoints = [last.x, last.y, currentMouse.x, currentMouse.y];
      this.previewLine = new Konva.Line({
        points: linePoints,
        stroke: '#93c5fd',
        strokeWidth: 1,
        dash: [4, 4],
        listening: false,
      });
      this.layer.add(this.previewLine);
    }

    // Draw points
    for (const p of points) {
      const circle = new Konva.Circle({
        x: p.x,
        y: p.y,
        radius: 4,
        fill: '#ffffff',
        stroke: '#2563eb',
        strokeWidth: 2,
        listening: false,
      });
      this.pointCircles.push(circle);
      this.layer.add(circle);

      // Draw handles if they exist
      if (p.handleOut) {
        const hx = p.x + p.handleOut.x;
        const hy = p.y + p.handleOut.y;
        const line = new Konva.Line({
          points: [p.x, p.y, hx, hy],
          stroke: '#94a3b8',
          strokeWidth: 1,
          listening: false,
        });
        this.handleLines.push(line);
        this.layer.add(line);

        const hCircle = new Konva.Circle({
          x: hx, y: hy,
          radius: 3,
          fill: '#2563eb',
          stroke: '#ffffff',
          strokeWidth: 1,
          listening: false,
        });
        this.handleCircles.push(hCircle);
        this.layer.add(hCircle);
      }

      if (p.handleIn) {
        const hx = p.x + p.handleIn.x;
        const hy = p.y + p.handleIn.y;
        const line = new Konva.Line({
          points: [p.x, p.y, hx, hy],
          stroke: '#94a3b8',
          strokeWidth: 1,
          listening: false,
        });
        this.handleLines.push(line);
        this.layer.add(line);

        const hCircle = new Konva.Circle({
          x: hx, y: hy,
          radius: 3,
          fill: '#2563eb',
          stroke: '#ffffff',
          strokeWidth: 1,
          listening: false,
        });
        this.handleCircles.push(hCircle);
        this.layer.add(hCircle);
      }
    }

    // Close indicator
    if (nearFirstPoint && points.length >= 3) {
      this.closeIndicator = new Konva.Circle({
        x: points[0].x,
        y: points[0].y,
        radius: 8,
        fill: 'rgba(37, 99, 235, 0.15)',
        stroke: '#2563eb',
        strokeWidth: 1,
        listening: false,
      });
      this.layer.add(this.closeIndicator);
    }

    this.layer.batchDraw();
  }

  clear(): void {
    this.clearAll();
    this.layer.batchDraw();
  }

  private clearAll(): void {
    this.path?.destroy();
    this.path = null;
    this.previewLine?.destroy();
    this.previewLine = null;
    this.closeIndicator?.destroy();
    this.closeIndicator = null;
    for (const c of this.pointCircles) c.destroy();
    this.pointCircles = [];
    for (const l of this.handleLines) l.destroy();
    this.handleLines = [];
    for (const c of this.handleCircles) c.destroy();
    this.handleCircles = [];
  }
}
